import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Button } from './index';
import { ApiError } from '../services/api';

interface AddPropertyFormProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: { address: string; rent: number; listingType: 'rent' | 'sale'; site: 'manual'; url?: string }) => Promise<string>;
  onCreated: (id: string) => void;
}

export function AddPropertyForm({ isOpen, onClose, onCreate, onCreated }: AddPropertyFormProps) {
  const { t } = useTranslation();
  const [listingType, setListingType] = useState<'rent' | 'sale'>('rent');
  const [address, setAddress] = useState('');
  const [price, setPrice] = useState('');
  const [url, setUrl] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setListingType('rent');
    setAddress('');
    setPrice('');
    setUrl('');
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    const trimmedAddress = address.trim();
    if (!trimmedAddress) { setError(t('app.addPropertyForm.addressRequired')); return; }

    const parsedPrice = parseFloat(price);
    if (isNaN(parsedPrice) || parsedPrice < 0) { setError(t('app.addPropertyForm.invalidPrice')); return; }

    const trimmedUrl = url.trim();
    if (trimmedUrl && !/^https?:\/\/.+/.test(trimmedUrl)) { setError(t('app.addPropertyForm.invalidUrl')); return; }

    setSubmitting(true);
    try {
      const id = await onCreate({
        address: trimmedAddress,
        rent: parsedPrice,
        listingType,
        site: 'manual',
        url: trimmedUrl || undefined,
      });
      reset();
      onClose();
      onCreated(id);
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        setError(err.message);
      } else if (err instanceof ApiError && err.status === 409) {
        setError(t('app.addPropertyForm.propertyExists'));
      } else {
        setError(t('app.addPropertyForm.failedToAdd'));
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={t('app.addPropertyForm.modalTitle')}>
      <div className="add-property-hint">
        {t('app.addPropertyForm.hint')}
      </div>
      <form onSubmit={handleSubmit} className="add-property-form">
        <div className="add-property-field">
          <label className="add-property-label">{t('app.addPropertyForm.listingType')}</label>
          <div className="add-property-toggle">
            <button type="button" className={`toggle-btn${listingType === 'rent' ? ' active' : ''}`} onClick={() => setListingType('rent')}>{t('app.addPropertyForm.rent')}</button>
            <button type="button" className={`toggle-btn${listingType === 'sale' ? ' active' : ''}`} onClick={() => setListingType('sale')}>{t('app.addPropertyForm.sale')}</button>
          </div>
        </div>

        <div className="add-property-field">
          <label className="add-property-label">{t('app.addPropertyForm.addressLabel')}</label>
          <input
            type="text"
            className="add-property-input"
            placeholder={t('app.addPropertyForm.addressPlaceholder')}
            value={address}
            onChange={e => setAddress(e.target.value)}
            maxLength={500}
            autoFocus
            disabled={submitting}
          />
        </div>

        <div className="add-property-field">
          <label className="add-property-label">{listingType === 'rent' ? t('app.addPropertyForm.rentPriceLabel') : t('app.addPropertyForm.salePriceLabel')}</label>
          <input
            type="number"
            className="add-property-input"
            placeholder={listingType === 'rent' ? t('app.addPropertyForm.rentPlaceholder') : t('app.addPropertyForm.salePlaceholder')}
            value={price}
            onChange={e => setPrice(e.target.value)}
            min={0}
            step="any"
            disabled={submitting}
          />
        </div>

        <div className="add-property-field">
          <label className="add-property-label">{t('app.addPropertyForm.urlLabel')}</label>
          <input
            type="text"
            className="add-property-input"
            placeholder={t('app.addPropertyForm.urlPlaceholder')}
            value={url}
            onChange={e => setUrl(e.target.value)}
            disabled={submitting}
          />
        </div>

        {error && <div className="add-property-error">{error}</div>}

        <div className="add-property-actions">
          <Button variant="secondary" size="sm" onClick={handleClose} disabled={submitting}>{t('common.cancel')}</Button>
          <Button variant="primary" size="sm" type="submit" disabled={submitting}>
            {submitting ? t('app.addPropertyForm.adding') : t('app.addPropertyForm.addButton')}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
